import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Video, FileText, StickyNote, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

type ContentType = 'video' | 'article' | 'note';
type Sport = 'football' | 'soccer' | 'basketball' | 'baseball';

type Props = {
  open: boolean;
  onClose: () => void;
  coachId: string;
  teamId?: string;
  defaultSport?: Sport;
  rosterPlayerIds?: string[];
  rosterPlayers?: { user_id: string; username: string | null }[];
  editing?: any;
  onSaved?: (newId?: string) => void;
};

const TYPES: { id: ContentType; label: string; icon: any; color: string }[] = [
  { id: 'video', label: 'Video', icon: Video, color: 'text-red-400' },
  { id: 'article', label: 'Article', icon: FileText, color: 'text-blue-400' },
  { id: 'note', label: 'Note / Tip', icon: StickyNote, color: 'text-yellow-400' },
];

const SPORTS: Sport[] = ['football', 'soccer', 'basketball', 'baseball'];

function youtubeId(url: string): string | null {
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

export function AddContentModal({ open, onClose, coachId, teamId, defaultSport = 'football', rosterPlayers = [], editing, onSaved }: Props) {
  const { user } = useAuth();
  const [type, setType] = useState<ContentType>(editing?.type || 'video');
  const [title, setTitle] = useState(editing?.title || '');
  const [url, setUrl] = useState(editing?.url || '');
  const [description, setDescription] = useState(editing?.description || '');
  const [readTime, setReadTime] = useState<string>(editing?.read_time_minutes?.toString() || '');
  const [sport, setSport] = useState<Sport>((editing?.sport as Sport) || defaultSport);
  const [assignAll, setAssignAll] = useState(editing?.assigned_to == null);
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>(editing?.assigned_to || []);
  const [fetchingMeta, setFetchingMeta] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setType(editing?.type || 'video');
    setTitle(editing?.title || '');
    setUrl(editing?.url || '');
    setDescription(editing?.description || '');
    setReadTime(editing?.read_time_minutes?.toString() || '');
    setSport((editing?.sport as Sport) || defaultSport);
    setAssignAll(editing?.assigned_to == null);
    setSelectedPlayers(editing?.assigned_to || []);
  }, [open, editing, defaultSport]);

  const fetchYouTubeMeta = async (videoUrl: string) => {
    if (!videoUrl || !youtubeId(videoUrl)) return;
    setFetchingMeta(true);
    try {
      const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(videoUrl)}&format=json`);
      if (res.ok) {
        const data = await res.json();
        if (data.title && !title) setTitle(data.title);
      }
    } catch {
      // ignore
    } finally {
      setFetchingMeta(false);
    }
  };

  const togglePlayer = (id: string) => {
    setSelectedPlayers((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('Title required');
      return;
    }
    if ((type === 'video' || type === 'article') && !url.trim()) {
      toast.error('URL required');
      return;
    }
    if (type === 'note' && description.length > 500) {
      toast.error('Note must be 500 characters or less');
      return;
    }
    setSaving(true);
    const payload: any = {
      coach_id: coachId,
      type,
      title: title.trim(),
      sport,
      url: type === 'note' ? null : url.trim(),
      description: description.trim() || null,
      read_time_minutes: type === 'article' && readTime ? parseInt(readTime) : null,
      team_id: teamId || null,
      thumbnail_url:
        type === 'video' && youtubeId(url)
          ? `https://i.ytimg.com/vi/${youtubeId(url)}/hqdefault.jpg`
          : null,
      assigned_to: assignAll ? null : selectedPlayers,
    };

    let error;
    let newId: string | undefined;
    if (editing) {
      const res = await supabase.from('coach_content' as any).update(payload).eq('id', editing.id);
      error = res.error;
    } else {
      const res = await supabase.from('coach_content' as any).insert(payload).select('id').single();
      error = res.error;
      newId = (res.data as any)?.id;
    }

    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(editing ? 'Content updated' : 'Content published!');
    onSaved?.(newId);
    onClose();
  };

  // Lock background scroll + prevent background interaction while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Keep the focused field visible above the mobile keyboard.
  const handleFieldFocus = (e: React.FocusEvent<HTMLElement>) => {
    const el = e.currentTarget;
    setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 250);
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[9999]"
            style={{ background: 'rgba(0, 0, 0, 0.75)' }}
          />
          <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6 pointer-events-none">
          <motion.div
            initial={{ opacity: 0, y: 18, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="pointer-events-auto w-full max-w-md h-auto flex flex-col overflow-hidden glass-card shadow-2xl"
            style={{ background: 'rgba(15, 20, 40, 0.98)', maxHeight: 'min(85dvh, 85vh)' }}
          >

            <div className="flex-shrink-0 sticky top-0 flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="font-display font-bold text-base">
                {editing ? 'Edit Content' : 'Add Content'}
              </h3>
              <button onClick={onClose} className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 space-y-5 pb-6">

              {/* Type selector */}
              <div className="grid grid-cols-3 gap-2">
                {TYPES.map((t) => {
                  const Icon = t.icon;
                  const active = type === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setType(t.id)}
                      className={`p-3 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                        active ? 'border-primary bg-primary/10' : 'border-white/10 hover:border-white/20'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${active ? 'text-primary' : t.color}`} />
                      <span className="text-xs font-display font-bold">{t.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* URL field for video/article */}
              {(type === 'video' || type === 'article') && (
                <div>
                  <Label htmlFor="content-url" className="text-xs font-display font-bold mb-1.5 block">URL</Label>
                  <div className="relative">
                    <Input
                      id="content-url"
                      placeholder={type === 'video' ? 'https://youtube.com/watch?v=...' : 'https://...'}
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onFocus={handleFieldFocus}
                      onBlur={() => type === 'video' && fetchYouTubeMeta(url)}
                      className="font-body"
                    />
                    {fetchingMeta && (
                      <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-foreground/50" />
                    )}
                  </div>
                </div>
              )}

              {/* Title */}
              <div>
                <Label htmlFor="content-title" className="text-xs font-display font-bold mb-1.5 block">Title</Label>
                <Input
                  id="content-title"
                  placeholder="What is this?"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onFocus={handleFieldFocus}
                  maxLength={120}
                  className="font-body"
                />
              </div>

              {/* Description / Note */}
              <div>
                <Label htmlFor="content-description" className="text-xs font-display font-bold mb-1.5 block">
                  {type === 'note' ? 'Note (max 500 chars)' : 'Description'}
                  {type === 'note' && (
                    <span className="text-foreground/40 font-body ml-2">
                      {description.length}/500
                    </span>
                  )}
                </Label>
                <Textarea
                  id="content-description"
                  placeholder={type === 'note' ? 'Quick tip or drill instruction...' : 'Brief description...'}
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, type === 'note' ? 500 : 1000))}
                  onFocus={handleFieldFocus}
                  className="font-body h-[90px] min-h-[90px] max-h-[90px] resize-none"
                />
              </div>


              {/* Read time for articles */}
              {type === 'article' && (
                <div>
                  <Label htmlFor="content-readtime" className="text-xs font-display font-bold mb-1.5 block">Estimated read time (min)</Label>
                  <Input
                    id="content-readtime"
                    type="number"
                    min={1}
                    max={120}
                    placeholder="5"
                    value={readTime}
                    onChange={(e) => setReadTime(e.target.value)}
                    className="font-body w-24"
                  />
                </div>
              )}

              {/* Sport tag — visually separated from the page's own sport tabs */}
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                <Label className="text-xs font-display font-bold mb-2 block">Sport for this content</Label>
                <div className="grid grid-cols-4 gap-1.5">
                  {SPORTS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSport(s)}
                      className={`py-1.5 rounded-lg text-xs font-display font-bold border transition-all capitalize ${
                        sport === s
                          ? 'border-primary bg-primary/10 text-foreground'
                          : 'border-white/10 text-foreground/60 hover:border-white/20'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Assignment */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-display font-bold">Share with whole roster</Label>
                  <Switch checked={assignAll} onCheckedChange={setAssignAll} />
                </div>
                {!assignAll && (
                  <div className="space-y-1 max-h-40 overflow-y-auto p-2 border border-white/10 rounded-lg">
                    {rosterPlayers.length === 0 ? (
                      <p className="text-xs text-foreground/50 text-center py-2">No players on roster</p>
                    ) : (
                      rosterPlayers.map((p) => (
                        <label key={p.user_id} className="flex items-center gap-2 p-1.5 hover:bg-white/5 rounded cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedPlayers.includes(p.user_id)}
                            onChange={() => togglePlayer(p.user_id)}
                            className="accent-primary"
                          />
                          <span className="text-sm font-body">{p.username || 'Unknown'}</span>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            <div
              className="flex-shrink-0 sticky bottom-0 px-4 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-white/10"
              style={{ background: 'rgba(15, 20, 40, 0.98)' }}
            >
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full font-display font-bold h-11 text-base"
                style={{ backgroundColor: '#3b6ef8' }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Publish to Players'}
              </Button>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}
